import React from 'react';
import { Box, Theme } from '@mui/material';
import { LoadingState } from '@insights-ltd/design-library/utils';
import { ButtonV2, TextV2 } from '@insights-ltd/design-library/components';
import { useTranslation } from 'react-i18next';

type LoadMoreButtonProps = {
  isFetching: boolean | undefined;
  loadMore: () => void;
  loadingState: LoadingState;
};

const LoadMoreButton = ({
  isFetching,
  loadMore,
  loadingState,
}: LoadMoreButtonProps): React.ReactElement => {
  const { t } = useTranslation();

  const commonButtonProps = {
    variant: 'text' as const,
    onClick: loadMore,
    sx: (theme: Theme) => ({
      height: theme.spacing(2.5),
      paddingLeft: theme.spacing(2),
      justifyContent: 'flex-start',
      '&,&:hover': { textDecoration: 'none' },
      fontWeight: 400,
      '&.Mui-disabled': { color: theme.palette.grey[600], opacity: 1 },
    }),
  };

  const renderButton = () => {
    if (isFetching) {
      return (
        <ButtonV2
          {...commonButtonProps}
          disabled={true}
          buttonText={t('ui.common.pagination.loading-pages')}
        />
      );
    }
    if (loadingState === 'success' || loadingState === 'idle') {
      return (
        <ButtonV2
          {...commonButtonProps}
          buttonText={t('ui.common.pagination.load-more-pages')}
        />
      );
    }
    return (
      <ButtonV2
        {...commonButtonProps}
        variant="text"
        buttonText={
          <>
            <TextV2 color="error">
              {t('ui.common.pagination.loading.error.load-failed')}
            </TextV2>
            <TextV2 color="primary">
              &nbsp;{t('ui.common.pagination.loading.error.retry.button')}
            </TextV2>
          </>
        }
      />
    );
  };

  return (
    <Box
      sx={(theme) => ({
        marginRight: theme.spacing(2),
        flexWrap: 'nowrap',
        marginLeft: theme.spacing(0.5),
        borderLeft: `solid 1px ${theme.palette.grey[500]}`,
      })}
    >
      {renderButton()}
    </Box>
  );
};

export default LoadMoreButton;
